import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  DatePicker,
  message,
  Row,
  Col,
  Typography,
  Alert,
  Spin,
  Tag,
  Modal,
  InputNumber,
  Select,
  Icon
} from 'antd';
import moment from 'moment';
import { apiGet, apiPost } from './utils/apiService';
import { fetchFullTankEventsForPeriod, fetchDailyFuelDataReport } from './utils/fuelService';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Classify a meter name string into one of KWH | KMS | HOURS | null
const classifyMeter = (name) => {
  if (!name || typeof name !== 'string') return null;
  const n = name.toUpperCase();
  if (n.includes('KWH') || n.includes('KILOWATT')) return 'KWH';
  if (n.includes('KM') || n.includes('KMS') || n.includes('ODO') || n.includes('DISTANCE')) return 'KMS';
  if (n.includes('HOUR') || n.includes('HRS')) return 'HOURS';
  return null;
};

// Pick a unit from a set/array of meter types using priority KWH > KMS > HOURS
// Returns 'L/KWH' | 'L/KM' | 'L/HOURS' | null
const pickPriorityUnit = (meterTypes) => {
  const set = new Set(meterTypes);
  if (set.has('KWH')) return 'L/KWH';
  if (set.has('KMS')) return 'L/KM';
  if (set.has('HOURS')) return 'L/HOURS';
  return null;
};

// Helper function to extract calculated reading from mileage
// Prefers calculatedReading from meterReadingRecord (cumulative, always increasing)
// Falls back to reading (physical meter reading, may reset on meter change)
const getCalculatedReading = (mileage) => {
  if (!mileage) return null;
  // Check for calculatedReading in meterReadingRecord first
  if (mileage.meterReadingRecord && 
      mileage.meterReadingRecord.calculatedReading !== null && 
      mileage.meterReadingRecord.calculatedReading !== undefined) {
    return mileage.meterReadingRecord.calculatedReading;
  }
  // Fall back to regular reading
  return mileage.reading;
};

// Import HDI calculation functions from ConsumptionRateAnalyzer
const highestDensityInterval = (values, coverage, centerHint = null) => {
  if (values.length === 0) {
    return [NaN, NaN, 0];
  }

  const n = values.length;
  let k = Math.ceil(coverage * n);
  k = Math.min(Math.max(k, 1), n);

  const vSorted = [...values].sort((a, b) => a - b);
  let bestI = 0;
  let bestWidth = Infinity;
  const ties = [];

  for (let i = 0; i <= n - k; i++) {
    const width = vSorted[i + k - 1] - vSorted[i];
    if (width < bestWidth - 1e-15) {
      bestWidth = width;
      bestI = i;
      ties.length = 0;
      ties.push(i);
    } else if (Math.abs(width - bestWidth) <= 1e-15) {
      ties.push(i);
    }
  }

  if (centerHint === null) {
    centerHint = median(values);
  }
  if (ties.length > 1) {
    bestI = ties.reduce((best, idx) => {
      const midpoint = (vSorted[idx] + vSorted[idx + k - 1]) / 2.0;
      const bestMidpoint = (vSorted[best] + vSorted[best + k - 1]) / 2.0;
      return Math.abs(midpoint - centerHint) < Math.abs(bestMidpoint - centerHint) ? idx : best;
    }, ties[0]);
  }

  const lower = vSorted[bestI];
  const upper = vSorted[bestI + k - 1];
  const count = values.filter(v => v >= lower && v <= upper).length;
  return [lower, upper, count];
};

const adaptiveCoverage = (n) => {
  if (n < 20) return 0.30;
  if (n < 50) return 0.40;
  return 0.50;
};

// Helper to create empty stats object
const createEmptyStats = (n_numeric = 0) => ({
  n_numeric,
  hdi_average: NaN,
  std_dev: NaN,
  cv_percent: NaN,
  hdi_lower: NaN,
  hdi_upper: NaN,
  hdi_range: NaN,
  coverage_used: NaN,
  outliers_count: 0,
  outliers_percent: NaN,
  mean: NaN,
});

const median = (arr) => {
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

const computeStatsHDIAdaptive = (series, dropZero = false) => {
  const nBefore = series.length;
  
  let numericValues = series
    .map(val => {
      const num = parseFloat(val);
      return isNaN(num) ? null : num;
    })
    .filter(val => val !== null);

  if (dropZero) {
    numericValues = numericValues.filter(val => val !== 0);
  }

  if (numericValues.length === 0) {
    return {
      n_numeric: 0,
      hdi_average: NaN,
      std_dev: NaN,
      cv_percent: NaN,
      hdi_lower: NaN,
      hdi_upper: NaN,
      hdi_range: NaN,
      coverage_used: NaN,
      outliers_count: 0,
      outliers_percent: NaN,
      mean: NaN,
    };
  }

  const n = numericValues.length;
  const coverage = adaptiveCoverage(n);
  const centerHint = median(numericValues);
  const [lower, upper, cnt] = highestDensityInterval(numericValues, coverage, centerHint);

  const windowVals = numericValues.filter(v => v >= lower && v <= upper);
  const hdiAvg = windowVals.length > 0
    ? windowVals.reduce((sum, val) => sum + val, 0) / windowVals.length
    : NaN;

  // Calculate additional statistics
  const mean = numericValues.reduce((sum, val) => sum + val, 0) / n;
  
  // Standard deviation
  const variance = numericValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / n;
  const stdDev = Math.sqrt(variance);
  
  // Coefficient of variation (CV) - expressed as percentage
  const cvPercent = mean !== 0 ? (stdDev / Math.abs(mean)) * 100 : NaN;
  
  // HDI range width
  const hdiRange = upper - lower;
  
  // Outliers - values outside HDI bounds
  const outliersCount = numericValues.filter(v => v < lower || v > upper).length;
  const outliersPercent = (outliersCount / n) * 100;

  return {
    n_numeric: n,
    hdi_average: hdiAvg,
    std_dev: stdDev,
    cv_percent: cvPercent,
    hdi_lower: lower,
    hdi_upper: upper,
    hdi_range: hdiRange,
    coverage_used: coverage * 100, // as percentage
    outliers_count: outliersCount,
    outliers_percent: outliersPercent,
    mean: mean,
  };
};

function HDIConsumptionComparison() {
  const [assetModels, setAssetModels] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingModels, setLoadingModels] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [editStandardRate, setEditStandardRate] = useState(null);
  const [editUnit, setEditUnit] = useState('KM/L');
  const [assetTypes, setAssetTypes] = useState([]);
  const [assetSubTypes, setAssetSubTypes] = useState([]);
  const [selectedAssetType, setSelectedAssetType] = useState(null);
  const [selectedAssetSubType, setSelectedAssetSubType] = useState(null);
  const [selectedAssetModel, setSelectedAssetModel] = useState(null);

  useEffect(() => {
    loadAssetModels();
  }, []);

  const loadAssetModels = async () => {
    setLoadingModels(true);
    try {
      // Fetch all asset models directly from API
      const modelsResult = await apiGet('/asset-model', {
        headers: {
          'Accept': '*/*',
        },
      });

      if (!modelsResult.success) {
        message.error(`Failed to load asset models: ${modelsResult.error}`);
        setLoadingModels(false);
        return;
      }

      // Log the response structure to understand it
      console.log('Asset Model API Response:', modelsResult.data);
      console.log('Response type:', typeof modelsResult.data);
      console.log('Is array:', Array.isArray(modelsResult.data));
      
      // Handle different response structures
      let modelsData = [];
      if (Array.isArray(modelsResult.data)) {
        modelsData = modelsResult.data;
      } else if (modelsResult.data && Array.isArray(modelsResult.data.data)) {
        // Response is {data: Array, count: number}
        modelsData = modelsResult.data.data;
        console.log(`Extracted ${modelsData.length} models from response.data`);
      } else if (modelsResult.data && Array.isArray(modelsResult.data.models)) {
        modelsData = modelsResult.data.models;
      } else if (modelsResult.data && typeof modelsResult.data === 'object') {
        // If it's an object, try to extract array from common property names
        const keys = Object.keys(modelsResult.data);
        console.log('Response object keys:', keys);
        // Try to find an array property
        for (const key of keys) {
          if (Array.isArray(modelsResult.data[key])) {
            modelsData = modelsResult.data[key];
            console.log(`Found array in property: ${key}`);
            break;
          }
        }
      }
      
      if (!Array.isArray(modelsData)) {
        console.error('Could not extract array from response. Full response:', modelsResult.data);
        message.error('Unexpected response format from asset model API');
        setLoadingModels(false);
        return;
      }
      
      console.log(`Extracted ${modelsData.length} models from API response`);

      // Map models data to our format
      // standardFuelConsumptionRate is an array of objects with fuelQty and perType
      // A model can have multiple standard rates (e.g., both L/HOURS and L/KWH)
      const allModels = modelsData.map(model => {
        const standardRates = []; // Array of { rate, unit } objects
        
        // Extract from array structure: standardFuelConsumptionRate is an array
        if (model.standardFuelConsumptionRate && Array.isArray(model.standardFuelConsumptionRate) && model.standardFuelConsumptionRate.length > 0) {
          model.standardFuelConsumptionRate.forEach(rateObj => {
            const rate = rateObj.fuelQty !== null && rateObj.fuelQty !== undefined ? rateObj.fuelQty : null;
            
            if (rate === null || isNaN(rate) || rate <= 0) {
              return; // Skip invalid rates
            }
            
            // Extract unit from perType.name
            // Standard consumption rate is always in "Litres per unit" format (L/KM, L/HOURS, L/KWH)
            if (rateObj.perType && rateObj.perType.name) {
              const unitName = rateObj.perType.name.toUpperCase();
              let standardUnit = null;
              
              // Map unit names to standard format (Litres per unit)
              if (unitName === 'KMS' || unitName === 'KM' || unitName.includes('KM')) {
                standardUnit = 'L/KM';
              } else if (unitName === 'HOURS' || unitName === 'HOUR' || unitName === 'HRS' || unitName.includes('HOUR')) {
                standardUnit = 'L/HOURS';
              } else if (unitName === 'KWH' || unitName.includes('KWH')) {
                standardUnit = 'L/KWH';
              } else {
                // Default to L/ prefix if unit name is provided
                standardUnit = `L/${unitName}`;
              }
              
              if (standardUnit) {
                standardRates.push({ rate, unit: standardUnit });
              }
            }
          });
        }
        
        // For backward compatibility, keep the first rate as the primary one
        const primaryRate = standardRates.length > 0 ? standardRates[0] : null;
        
        // Only log models without rates if they're vehicle/asset types (not office equipment)
        // Skip logging for office equipment, furniture, etc.
        const isVehicleOrAsset = model.name && (
          model.name.toUpperCase().includes('TATA') ||
          model.name.toUpperCase().includes('TOYOTA') ||
          model.name.toUpperCase().includes('HONDA') ||
          model.name.toUpperCase().includes('HYUNDAI') ||
          model.name.toUpperCase().includes('MERCEDES') ||
          model.name.toUpperCase().includes('MAHINDRA') ||
          model.name.toUpperCase().includes('ALTO') ||
          model.name.toUpperCase().includes('INNOVA') ||
          model.name.toUpperCase().includes('CRETA') ||
          model.name.toUpperCase().includes('YODHA') ||
          model.name.toUpperCase().includes('FURIO')
        );
        
        if (isVehicleOrAsset && standardRates.length === 0) {
          console.log(`Model ${model.name} - No standard rates defined, Raw:`, model.standardFuelConsumptionRate);
        }
        
        // Fuel type on the asset model. Can be a populated object {_id, name},
        // a raw ObjectId string, or absent (null) for non-fuel assets.
        const fuelTypeRaw = model.fuelType;
        const fuelTypeId = fuelTypeRaw
          ? (typeof fuelTypeRaw === 'string' ? fuelTypeRaw : (fuelTypeRaw._id || null))
          : null;
        const fuelTypeName = fuelTypeRaw && typeof fuelTypeRaw === 'object' ? (fuelTypeRaw.name || null) : null;

        return {
          modelId: model._id,
          modelName: model.name || `Model ${model._id}`,
          standardConsumptionRate: primaryRate ? primaryRate.rate : null,
          standardConsumptionRateUnit: primaryRate ? primaryRate.unit : null,
          standardRates: standardRates, // All standard rates for this model
          fuelTypeId: fuelTypeId,
          fuelTypeName: fuelTypeName,
          assetSubTypeId: model.assetSubType ? (typeof model.assetSubType === 'string' ? model.assetSubType : model.assetSubType._id) : null,
          assetSubTypeName: model.assetSubType ? (typeof model.assetSubType === 'string' ? null : (model.assetSubType.name || null)) : null,
        };
      });

      // Exclude asset models that have NO fuel type defined. These are non-fuel
      // assets (office equipment, furniture, etc.) that are not relevant to a fuel
      // consumption HDI report. Excluding them here also keeps them out of the
      // downstream IoT/mileage processing. Excluded models are logged and their
      // count is surfaced to the user so the exclusion is not silent.
      const modelsWithoutFuelType = allModels.filter(m => !m.fuelTypeId);
      const filteredModels = allModels.filter(m => m.fuelTypeId);
      if (modelsWithoutFuelType.length > 0) {
        console.log(
          `Excluded ${modelsWithoutFuelType.length} asset model(s) with no fuel type:`,
          modelsWithoutFuelType.map(m => m.modelName)
        );
      }
      console.log(`Total models: ${allModels.length}, included (with fuel type): ${filteredModels.length}, excluded (no fuel type): ${modelsWithoutFuelType.length}`);
      
      console.log('Models loaded:', filteredModels.map(m => ({ 
        name: m.modelName, 
        rate: m.standardConsumptionRate, 
        unit: m.standardConsumptionRateUnit,
        hasStandardRate: m.standardRates && m.standardRates.length > 0
      })));

      setAssetModels(filteredModels);
      const rateCount = filteredModels.filter(m => m.standardRates && m.standardRates.length > 0).length;
      
      // Load asset sub types and build hierarchy
      await loadAssetSubTypesAndTypes(filteredModels);

      const excludedNoFuelType = allModels.length - filteredModels.length;
      const excludedNote = excludedNoFuelType > 0 ? `, excluded ${excludedNoFuelType} with no fuel type` : '';
      message.success(`Loaded ${filteredModels.length} asset models (${rateCount} with standard rates, ${filteredModels.length - rateCount} without${excludedNote})`);
    } catch (error) {
      console.error('Error loading asset models:', error);
      message.error(`Error loading asset models: ${error.message}`);
    } finally {
      setLoadingModels(false);
    }
  };

  const loadAssetSubTypesAndTypes = async (models) => {
    try {
      // Fetch asset sub types
      const subTypesResult = await apiGet('/equipment-sub-type', {
        headers: {
          'Accept': '*/*',
        },
      });

      if (!subTypesResult.success) {
        console.warn(`Failed to load asset sub types: ${subTypesResult.error}`);
        return;
      }

      // Extract sub types array from response
      let subTypesData = [];
      if (Array.isArray(subTypesResult.data)) {
        subTypesData = subTypesResult.data;
      } else if (subTypesResult.data && Array.isArray(subTypesResult.data.data)) {
        subTypesData = subTypesResult.data.data;
      } else if (subTypesResult.data && Array.isArray(subTypesResult.data.subTypes)) {
        subTypesData = subTypesResult.data.subTypes;
      }

      console.log(`Loaded ${subTypesData.length} asset sub types`);

      // Build maps: subTypeId -> subType, subTypeId -> assetTypeId
      const subTypeMap = {};
      const subTypeToAssetTypeMap = {};
      const assetTypeMap = {};

      subTypesData.forEach(subType => {
        const subTypeId = subType._id;
        const assetTypeId = subType.assetType ? (typeof subType.assetType === 'string' ? subType.assetType : subType.assetType._id) : null;
        const assetTypeName = subType.assetType ? (typeof subType.assetType === 'string' ? null : (subType.assetType.name || null)) : null;

        subTypeMap[subTypeId] = {
          id: subTypeId,
          name: subType.name || `Sub Type ${subTypeId}`,
          code: subType.code || null,
        };

        if (assetTypeId) {
          subTypeToAssetTypeMap[subTypeId] = assetTypeId;
          if (!assetTypeMap[assetTypeId]) {
            assetTypeMap[assetTypeId] = {
              id: assetTypeId,
              name: assetTypeName || `Asset Type ${assetTypeId}`,
            };
          }
        }
      });

      // Update models with sub type and asset type information
      const updatedModels = models.map(model => {
        if (model.assetSubTypeId && subTypeMap[model.assetSubTypeId]) {
          const subType = subTypeMap[model.assetSubTypeId];
          const assetTypeId = subTypeToAssetTypeMap[model.assetSubTypeId];
          
          return {
            ...model,
            assetSubTypeName: subType.name,
            assetTypeId: assetTypeId || null,
            assetTypeName: assetTypeId && assetTypeMap[assetTypeId] ? assetTypeMap[assetTypeId].name : null,
          };
        }
        return model;
      });

      setAssetModels(updatedModels);

      // Build unique lists for filters
      const uniqueAssetTypes = [];
      const uniqueAssetSubTypes = [];
      const typeMap = {};

      updatedModels.forEach(model => {
        if (model.assetTypeId && model.assetTypeName) {
          if (!typeMap[model.assetTypeId]) {
            typeMap[model.assetTypeId] = {
              id: model.assetTypeId,
              name: model.assetTypeName,
              subTypes: new Set(),
            };
            uniqueAssetTypes.push({ id: model.assetTypeId, name: model.assetTypeName });
          }
          if (model.assetSubTypeId && model.assetSubTypeName) {
            typeMap[model.assetTypeId].subTypes.add(model.assetSubTypeId);
            if (!uniqueAssetSubTypes.find(st => st.id === model.assetSubTypeId)) {
              uniqueAssetSubTypes.push({
                id: model.assetSubTypeId,
                name: model.assetSubTypeName,
                assetTypeId: model.assetTypeId,
              });
            }
          }
        } else if (model.assetSubTypeId && model.assetSubTypeName) {
          // Sub type without asset type
          if (!uniqueAssetSubTypes.find(st => st.id === model.assetSubTypeId)) {
            uniqueAssetSubTypes.push({
              id: model.assetSubTypeId,
              name: model.assetSubTypeName,
              assetTypeId: null,
            });
          }
        }
      });

      // Sort by name
      uniqueAssetTypes.sort((a, b) => a.name.localeCompare(b.name));
      uniqueAssetSubTypes.sort((a, b) => a.name.localeCompare(b.name));

      setAssetTypes(uniqueAssetTypes);
      setAssetSubTypes(uniqueAssetSubTypes);

      console.log(`Loaded ${uniqueAssetTypes.length} asset types and ${uniqueAssetSubTypes.length} asset sub types`);
    } catch (error) {
      console.error('Error loading asset sub types:', error);
      message.warning(`Failed to load asset sub types: ${error.message}`);
    }
  };

  const processData = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.warning('Please select a date range');
      return;
    }

    setLoading(true);
    const resultsData = [];

    try {
      const startTimestamp = dateRange[0].startOf('day').valueOf();
      const endTimestamp = dateRange[1].endOf('day').valueOf();
      
      // Build server-side filters from the selected Asset Type / Sub Type / Model.
      // Passing these to the report endpoint scopes the query on the backend so we
      // don't pull IoT data for every asset in the fleet (the previous behavior,
      // which made the query payload very large). When nothing is selected the
      // arrays are empty and the backend returns all assets (unchanged behavior).
      const assetTypesFilter = selectedAssetType ? [selectedAssetType] : [];
      const assetSubTypesFilter = selectedAssetSubType ? [selectedAssetSubType] : [];
      const assetModelsFilter = selectedAssetModel ? [selectedAssetModel] : [];

      // Restrict processing to the models matching the selected filters. This keeps
      // the result rows aligned with the (now filtered) query and avoids producing
      // empty rows for models the user filtered out.
      const modelsToProcess = assetModels.filter(model => {
        if (selectedAssetType && model.assetTypeId !== selectedAssetType) return false;
        if (selectedAssetSubType && model.assetSubTypeId !== selectedAssetSubType) return false;
        if (selectedAssetModel && model.modelId !== selectedAssetModel) return false;
        return true;
      });

      console.log('=== Starting HDI Calculation ===');
      console.log(`Date Range: ${dateRange[0].format('YYYY-MM-DD')} to ${dateRange[1].format('YYYY-MM-DD')}`);
      console.log(`Timestamps: ${startTimestamp} to ${endTimestamp}`);
      console.log(`Filters -> assetTypes: ${JSON.stringify(assetTypesFilter)}, assetSubTypes: ${JSON.stringify(assetSubTypesFilter)}, assetModels: ${JSON.stringify(assetModelsFilter)}`);
      console.log(`Processing ${modelsToProcess.length} of ${assetModels.length} asset models`);

      // Fetch IoT data for the selected assets in one call
      console.log('Fetching IoT data...');
      const iotResult = await fetchDailyFuelDataReport(startTimestamp, endTimestamp, {
        assets: null,
        locations: ['all'],
        subLocations: ['all'],
        assetTypes: assetTypesFilter,
        assetSubTypes: assetSubTypesFilter,
        assetModels: assetModelsFilter,
        frequency: 'Daily',
      });

      if (!iotResult.success || !iotResult.data || iotResult.data.length === 0) {
        message.error(`Failed to fetch IoT data: ${iotResult.error || 'No data returned'}`);
        setLoading(false);
        return;
      }

      console.log(`Fetched ${iotResult.data.length} IoT daily records`);

      // Fetch full tank to full tank data (mileage report)
      console.log('Fetching full tank to full tank data (mileage report)...');
      const periodFrom = moment(startTimestamp).toISOString();
      const periodTo = moment(endTimestamp).toISOString();
      
      const mileageResult = await apiPost('/fuel/mileage-report', {
        asset: null,
        location: 'all',
        subLocation: 'all',
        periodFrom: periodFrom,
        periodTo: periodTo,
      });

      if (!mileageResult.success) {
        console.warn(`Failed to fetch mileage report: ${mileageResult.error}`);
      } else {
        console.log(`Fetched ${mileageResult.data ? mileageResult.data.length : 0} fuel entries from mileage report`);
      }

      // Group IoT data by assetModel._id
      const iotDataByModel = {};
      iotResult.data.forEach(record => {
        const assetModel = record.asset && record.asset.assetModel;
        if (!assetModel) return;
        
        const modelId = typeof assetModel === 'string' ? assetModel : assetModel._id;
        if (!modelId) return;

        if (!iotDataByModel[modelId]) {
          iotDataByModel[modelId] = [];
        }
        iotDataByModel[modelId].push(record);
      });

      console.log(`Grouped IoT data into ${Object.keys(iotDataByModel).length} models`);

      // Group mileage report data by asset ID first, then by model
      const fullTankDataByModel = {};
      if (mileageResult.success && mileageResult.data && mileageResult.data.length > 0) {
        console.log('Sample mileage report entry:', mileageResult.data[0]);
        
        // Helper function to extract asset ID
        const getAssetId = (entry) => {
          if (!entry || !entry.asset) return null;
          const asset = entry.asset;
          // Asset can be either a string (ID) or an object with _id property
          if (typeof asset === 'string') {
            return asset;
          } else if (asset && typeof asset === 'object' && asset._id) {
            return asset._id;
          }
          return null;
        };

        // First, sort ALL entries by asset ID, then by date within each asset
        const allEntries = mileageResult.data.slice(); // Create a copy
        allEntries.sort((a, b) => {
          // First sort by asset ID
          const assetIdA = getAssetId(a) || '';
          const assetIdB = getAssetId(b) || '';
          
          if (assetIdA !== assetIdB) {
            return assetIdA.localeCompare(assetIdB);
          }
          
          // If same asset, sort by date
          const dateA = moment(a.date || a.datetime);
          const dateB = moment(b.date || b.datetime);
          return dateA.valueOf() - dateB.valueOf();
        });

        // Group by asset ID after sorting
        const entriesByAsset = {};
        allEntries.forEach(entry => {
          const assetId = getAssetId(entry);
          if (!assetId) {
            console.warn('Entry missing asset ID:', entry._id || entry.voucherNo);
            return;
          }

          if (!entriesByAsset[assetId]) {
            entriesByAsset[assetId] = [];
          }
          entriesByAsset[assetId].push(entry);
        });

        console.log(`Grouped mileage report into ${Object.keys(entriesByAsset).length} assets`);

        // Process each asset's entries to calculate consumption rates
        const allConsumptionRatesByModel = {};
        
        Object.entries(entriesByAsset).forEach(([assetId, entries]) => {
          // Get asset model for this asset
          const firstEntry = entries[0];
          const assetModel = firstEntry.assetModel;
          if (!assetModel) {
            console.warn(`Asset ${assetId} missing assetModel`);
            return;
          }
          
          const modelId = typeof assetModel === 'string' ? assetModel : assetModel._id;
          if (!modelId) {
            console.warn(`Asset ${assetId} has invalid assetModel`);
            return;
          }

          // Entries are already sorted by date (from the initial sort)
          // But let's ensure they're sorted just to be safe
          entries.sort((a, b) => {
            const dateA = moment(a.date || a.datetime);
            const dateB = moment(b.date || b.datetime);
            return dateA.valueOf() - dateB.valueOf();
          });

          // Verify all entries belong to the same asset using helper function
          const allSameAsset = entries.every(e => {
            const eAssetId = getAssetId(e);
            return eAssetId === assetId;
          });

          if (!allSameAsset) {
            console.warn(`Asset ID mismatch detected for asset ${assetId}`);
            return;
          }

          // Find full tank events for THIS ASSET ONLY within the date range
          // The API fetches entries up to the previous full tank event, so we can calculate rates for the first event too
          const fullTankEventsInRange = entries.filter(e => {
            // Double-check asset ID matches
            const eAssetId = getAssetId(e);
            if (eAssetId !== assetId) return false;
            
            // Only include full tank events
            if (e.isFullTank !== true) return false;
            
            // Filter to only include events within the date range (for reporting)
            const entryDate = moment(e.date || e.datetime);
            return entryDate.isBetween(moment(startTimestamp), moment(endTimestamp), null, '[]');
          });
          
          if (fullTankEventsInRange.length === 0) {
            // No full tank events in the date range for this asset
            return;
          }

          console.log(`Asset ${assetId}: Found ${fullTankEventsInRange.length} full tank events in date range`);

          // Get ALL full tank events for this asset (including those outside date range)
          // This allows us to find the previous full tank event for the first event in the range
          const allFullTankEventsForAsset = entries.filter(e => {
            const eAssetId = getAssetId(e);
            return eAssetId === assetId && e.isFullTank === true;
          }).sort((a, b) => {
            const dateA = moment(a.date || a.datetime);
            const dateB = moment(b.date || b.datetime);
            return dateA.valueOf() - dateB.valueOf();
          });

          // Calculate consumption rate for each full tank event within the date range
          // Use allFullTankEventsForAsset to find previous event (even if outside date range)
          for (const currentFullTank of fullTankEventsInRange) {
            const currentFullTankDate = moment(currentFullTank.date || currentFullTank.datetime);
            
            // Find the previous full tank event from ALL events for this asset (including those outside date range)
            // This allows calculation for the first full tank event in the range
            let previousFullTank = null;
            for (let i = allFullTankEventsForAsset.length - 1; i >= 0; i--) {
              const event = allFullTankEventsForAsset[i];
              const eventDate = moment(event.date || event.datetime);
              if (eventDate.isBefore(currentFullTankDate)) {
                previousFullTank = event;
                break;
              }
            }
            
            // Skip if no previous full tank event found
            if (!previousFullTank) {
              continue;
            }
            
            const nextFullTank = currentFullTank; // Current full tank is the "next" one relative to previous
            
            // Verify both full tank events belong to the same asset
            const previousAssetId = getAssetId(previousFullTank);
            const nextAssetId = getAssetId(nextFullTank);
            
            if (previousAssetId !== assetId || nextAssetId !== assetId) {
              console.warn(`Asset ID mismatch in full tank events: previous=${previousAssetId}, next=${nextAssetId}, expected=${assetId}`);
              continue;
            }
            
            const previousDate = moment(previousFullTank.date || previousFullTank.datetime);
            const nextDate = moment(nextFullTank.date || nextFullTank.datetime);
            
            // Get meter readings (distance or hours) - try to find the right meter
            let previousMileage = null;
            let nextMileage = null;
            
            // Check if we can determine meter type from first entry
            const sampleMileage = previousFullTank.mileage && previousFullTank.mileage.length > 0 
              ? previousFullTank.mileage[0] 
              : null;
            
            if (sampleMileage) {
              const meterName = sampleMileage.name ? sampleMileage.name.toUpperCase() : '';
              const isKmBased = meterName.includes('KM') || meterName.includes('KMS') || 
                               meterName.includes('ODO') || meterName.includes('DISTANCE');
              const isHoursBased = meterName.includes('HOUR') || meterName.includes('HRS');
              const isKwhBased = meterName.includes('KWH') || meterName.includes('KILOWATT');
              
              // Find matching meter in both events
              if (isKmBased || isHoursBased || isKwhBased) {
                previousMileage = previousFullTank.mileage.find(m => {
                  const mName = m.name ? m.name.toUpperCase() : '';
                  return (isKmBased && (mName.includes('KM') || mName.includes('KMS') || mName.includes('ODO') || mName.includes('DISTANCE'))) ||
                         (isHoursBased && (mName.includes('HOUR') || mName.includes('HRS'))) ||
                         (isKwhBased && (mName.includes('KWH') || mName.includes('KILOWATT')));
                }) || previousFullTank.mileage[0];
                
                nextMileage = nextFullTank.mileage.find(m => {
                  const mName = m.name ? m.name.toUpperCase() : '';
                  return (isKmBased && (mName.includes('KM') || mName.includes('KMS') || mName.includes('ODO') || mName.includes('DISTANCE'))) ||
                         (isHoursBased && (mName.includes('HOUR') || mName.includes('HRS'))) ||
                         (isKwhBased && (mName.includes('KWH') || mName.includes('KILOWATT')));
                }) || nextFullTank.mileage[0];
              } else {
                // Fallback to first meter
                previousMileage = previousFullTank.mileage[0];
                nextMileage = nextFullTank.mileage && nextFullTank.mileage.length > 0 ? nextFullTank.mileage[0] : null;
              }
            }
            
            if (!previousMileage || !nextMileage) continue;
            
            // Use calculatedReading for run calculation (cumulative, handles meter resets)
            const previousReading = getCalculatedReading(previousMileage);
            const nextReading = getCalculatedReading(nextMileage);
            
            if (previousReading === null || nextReading === null || nextReading <= previousReading) {
              continue;
            }
            
            const runValue = nextReading - previousReading;
            
            // Get all fuel entries for THIS ASSET ONLY between these two full tank events
            // Filter by both date range AND asset ID to ensure we only get entries for this asset
            // EXCLUDE the previous full tank event - only include:
            // 1. The current full tank event (nextFullTank)
            // 2. Any non-full tank events between the two full tank events
            // Use ALL entries (including those outside date range) to capture entries between previous and current
            const fuelEntriesBetween = entries.filter(e => {
              // First verify asset ID matches
              const eAssetId = getAssetId(e);
              if (eAssetId !== assetId) {
                return false;
              }
              
              // Exclude the previous full tank event
              // We identify it by comparing _id or voucherNo
              const isPreviousFullTank = (e._id === previousFullTank._id) || 
                                          (e.voucherNo && previousFullTank.voucherNo && e.voucherNo === previousFullTank.voucherNo);
              if (isPreviousFullTank) {
                return false;
              }
              
              // Then check date range - include entries from after previous full tank to and including current full tank
              const entryDate = moment(e.date || e.datetime);
              return (entryDate.isAfter(previousDate) || entryDate.isSame(previousDate)) && 
                     (entryDate.isBefore(nextDate) || entryDate.isSame(nextDate));
            });
            
            // Sum all fuel entries (current full tank + non-full tank events between)
            // This excludes the previous full tank event's fuel quantity
            const fuelConsumed = fuelEntriesBetween.reduce((sum, e) => sum + (e.quantity || 0), 0);
            
            if (fuelConsumed <= 0 || runValue <= 0) continue;
            
            // Calculate consumption rate - always in L/Unit format
            // L/KM or L/HOURS = fuel_consumed / run_value
            const rateLPerUnit = fuelConsumed / runValue;
            
            if (rateLPerUnit > 0) {
              // Store rate with metadata for debugging
              const rateData = {
                rate: rateLPerUnit,
                assetId: assetId,
                assetCode: firstEntry.asset && firstEntry.asset.assetCode ? firstEntry.asset.assetCode : assetId,
                fromVoucher: previousFullTank.voucherNo || previousFullTank._id || 'N/A',
                toVoucher: nextFullTank.voucherNo || nextFullTank._id || 'N/A',
                fromDate: previousDate.format('YYYY-MM-DD HH:mm:ss'),
                toDate: nextDate.format('YYYY-MM-DD HH:mm:ss'),
                fuelConsumed: fuelConsumed,
                runValue: runValue,
                meterName: previousMileage.name || 'N/A',
                fuelEntries: fuelEntriesBetween.map(e => ({
                  voucherNo: e.voucherNo || e._id || 'N/A',
                  date: moment(e.date || e.datetime).format('YYYY-MM-DD HH:mm:ss'),
                  quantity: e.quantity || 0,
                  isFullTank: e.isFullTank || false,
                  assetId: getAssetId(e) || 'N/A'
                }))
              };
              
              // Group by model ID
              if (!allConsumptionRatesByModel[modelId]) {
                allConsumptionRatesByModel[modelId] = [];
              }
              allConsumptionRatesByModel[modelId].push(rateData);
            }
          }
        });
        
        // Store the rates by model
        Object.assign(fullTankDataByModel, allConsumptionRatesByModel);
        
        console.log(`Calculated full tank consumption rates for ${Object.keys(fullTankDataByModel).length} models from ${Object.keys(entriesByAsset).length} assets`);
      }

      // Process each asset model - create one row per standard rate.
      // Models without any standard rate get one row with a unit chosen from the
      // model's actual meters using priority KWH > KMS > HOURS.
      for (const model of modelsToProcess) {
        const hasStandardRates = model.standardRates && model.standardRates.length > 0;

        // No standard rates: derive HDI from the meter types actually present in
        // the manually-entered fuel entries. Group full-tank rates by classified
        // meter type (KWH/KMS/HOURS) and emit one row per group so a model with
        // multiple meter types produces multiple HDI rows.
        if (!hasStandardRates) {
          const fullTankRates = fullTankDataByModel[model.modelId] || [];

          // Group rates by classified meter type
          const ratesByMeterType = {}; // { KWH: [rateData], KMS: [...], HOURS: [...] }
          fullTankRates.forEach(rateData => {
            const rate = typeof rateData === 'object' ? rateData.rate : rateData;
            if (!(rate > 0)) return;
            const meterName = (typeof rateData === 'object' && rateData.meterName) ? rateData.meterName : null;
            const cls = classifyMeter(meterName);
            if (!cls) return; // Skip entries with no classifiable meter
            if (!ratesByMeterType[cls]) ratesByMeterType[cls] = [];
            ratesByMeterType[cls].push(rateData);
          });

          const meterTypesPresent = Object.keys(ratesByMeterType);
          console.log(`Model ${model.modelName} has no standard rate. Meter types from fuel entries: [${meterTypesPresent.join(', ')}]`);

          // No fuel entry meter info -> emit a placeholder row so the model is visible
          if (meterTypesPresent.length === 0) {
            resultsData.push({
              modelId: model.modelId,
              modelName: model.modelName,
              standardConsumptionRate: null,
              standardConsumptionRateUnit: null,
              hdiLPerUnit: null,
              hdiUnitPerL: null,
              fullTankHdiLPerUnit: null,
              fullTankHdiUnitPerL: null,
              dataPoints: 0,
              fullTankDataPoints: 0,
              standardRateMissing: true,
              errors: 'No meter unit defined in fuel entries',
            });
            continue;
          }

          // Emit one result row per meter type observed in the fuel entries
          meterTypesPresent.forEach(meterType => {
            const groupRates = ratesByMeterType[meterType];
            const unitToUse = meterType === 'KWH' ? 'L/KWH'
                            : meterType === 'KMS' ? 'L/KM'
                            : 'L/HOURS';

            const fullTankRatesLPerUnit = [];
            const fullTankRatesUnitPerL = [];
            const fullTankRatesMetadata = [];
            groupRates.forEach(rateData => {
              const rate = typeof rateData === 'object' ? rateData.rate : rateData;
              fullTankRatesLPerUnit.push(rate);
              fullTankRatesUnitPerL.push(1 / rate);
              fullTankRatesMetadata.push(typeof rateData === 'object' ? rateData : null);
            });

            const fullTankRatesLPerUnitFiltered = fullTankRatesLPerUnit.filter(r => r > 0);
            const fullTankRatesUnitPerLFiltered = fullTankRatesUnitPerL.filter(r => r > 0);

            let fullTankHdiLPerUnit = createEmptyStats(fullTankRatesLPerUnitFiltered.length);
            let fullTankHdiUnitPerL = createEmptyStats(fullTankRatesUnitPerLFiltered.length);
            if (fullTankRatesLPerUnitFiltered.length >= 5) {
              fullTankHdiLPerUnit = computeStatsHDIAdaptive(fullTankRatesLPerUnitFiltered, true);
            }
            if (fullTankRatesUnitPerLFiltered.length >= 5) {
              fullTankHdiUnitPerL = computeStatsHDIAdaptive(fullTankRatesUnitPerLFiltered, true);
            }

            resultsData.push({
              modelId: model.modelId,
              modelName: model.modelName,
              standardConsumptionRate: null,
              standardConsumptionRateUnit: unitToUse,
              hdiLPerUnit: null,
              hdiUnitPerL: null,
              fullTankHdiLPerUnit: fullTankHdiLPerUnit.hdi_average,
              fullTankHdiUnitPerL: fullTankHdiUnitPerL.hdi_average,
              dataPoints: 0,
              fullTankDataPoints: fullTankRatesLPerUnitFiltered.length,
              standardRateMissing: true,
              rawRatesLPerUnit: [],
              rawRatesUnitPerL: [],
              rawFullTankRatesLPerUnit: fullTankRatesLPerUnitFiltered,
              rawFullTankRatesUnitPerL: fullTankRatesUnitPerLFiltered,
              rawFullTankRatesMetadata: fullTankRatesLPerUnitFiltered.map(rate => {
                const idx = fullTankRatesLPerUnit.indexOf(rate);
                return idx >= 0 ? fullTankRatesMetadata[idx] : null;
              }),
              // IoT variability metrics (empty)
              iotStdDev: 0, iotCvPercent: 0, iotHdiLower: 0, iotHdiUpper: 0,
              iotHdiRange: 0, iotCoverageUsed: 0, iotOutliersCount: 0,
              iotOutliersPercent: 0, iotMean: 0,
              // Full tank variability metrics
              fullTankStdDev: fullTankHdiLPerUnit.std_dev,
              fullTankCvPercent: fullTankHdiLPerUnit.cv_percent,
              fullTankHdiLower: fullTankHdiLPerUnit.hdi_lower,
              fullTankHdiUpper: fullTankHdiLPerUnit.hdi_upper,
              fullTankHdiRange: fullTankHdiLPerUnit.hdi_range,
              fullTankCoverageUsed: fullTankHdiLPerUnit.coverage_used,
              fullTankOutliersCount: fullTankHdiLPerUnit.outliers_count,
              fullTankOutliersPercent: fullTankHdiLPerUnit.outliers_percent,
              fullTankMean: fullTankHdiLPerUnit.mean,
            });
          });

          continue; // Skip to next model
        }

        const iotModelData = iotDataByModel[model.modelId] || [];
        const fullTankRates = fullTankDataByModel[model.modelId] || [];
        console.log(`Processing model: ${model.modelName}, IoT records: ${iotModelData.length}, Full tank rates: ${fullTankRates.length}`);

        // Process IoT data - only for units that are available in IoT data (Hours and KM, not KWH yet)
        const iotRatesByUnit = {}; // { 'L/HOURS': { lPerUnit: [], unitPerL: [] }, 'L/KM': { ... } }
        
        // Initialize rates for each standard unit that's available in IoT data
        model.standardRates.forEach(stdRate => {
          const unit = stdRate.unit.toUpperCase();
          // Only process Hours and KM for IoT data (KWH not available yet)
          if (unit === 'L/HOURS' || unit === 'LTR/HOURS' || unit === 'L/HRS' || 
              unit === 'L/KM' || unit === 'L/KMS') {
            if (!iotRatesByUnit[stdRate.unit]) {
              iotRatesByUnit[stdRate.unit] = { lPerUnit: [], unitPerL: [] };
            }
          }
        });

        if (iotModelData.length > 0) {
          iotModelData.forEach((day, index) => {
            // Process each standard unit that's available in IoT data
            Object.keys(iotRatesByUnit).forEach(unit => {
              const unitUpper = unit.toUpperCase();
              
              if (unitUpper === 'L/KM' || unitUpper === 'L/KMS') {
                // Calculate L/KM = fuel_consumed / distance_km
                const distanceKm = day.distance_travelled ? (day.distance_travelled / 1000) : 0;
                const fuelConsumed = day.fuel_consumed || day.fuel_consumed_analog || 0;
                
                if (distanceKm > 0 && fuelConsumed > 0) {
                  const rateLPerKm = fuelConsumed / distanceKm;
                  const rateKmPerL = distanceKm / fuelConsumed;
                  
                  if (rateLPerKm > 0) iotRatesByUnit[unit].lPerUnit.push(rateLPerKm);
                  if (rateKmPerL > 0) iotRatesByUnit[unit].unitPerL.push(rateKmPerL);
                }
              } else if (unitUpper === 'L/HOURS' || unitUpper === 'LTR/HOURS' || unitUpper === 'L/HRS') {
                // Calculate L/HOURS = fuel_consumed / running_hours
                const fuelConsumed = day.fuel_consumed || day.fuel_consumed_analog || 0;
                let runningHours = 0;
                if (day.running_hours) {
                  runningHours = day.running_hours;
                } else if (day.ignition_time) {
                  runningHours = day.ignition_time / (1000 * 60 * 60); // Convert ms to hours
                }
                
                if (fuelConsumed > 0 && runningHours > 0) {
                  const rateLPerHour = fuelConsumed / runningHours;
                  const rateHourPerL = runningHours / fuelConsumed;
                  
                  if (rateLPerHour > 0) iotRatesByUnit[unit].lPerUnit.push(rateLPerHour);
                  if (rateHourPerL > 0) iotRatesByUnit[unit].unitPerL.push(rateHourPerL);
                }
              }
              // Note: L/KWH is not processed for IoT data yet (not available in IoT data)
            });
          });
        }

        // Process Full Tank data - group by standard unit type
        // Full tank rates can have different meter types (Hours, KM, KWH)
        // We need to match them to the standard units defined for this model
        const fullTankRatesByUnit = {}; // { 'L/HOURS': { rates: [], metadata: [] }, 'L/KM': { ... }, 'L/KWH': { ... } }
        
        // Initialize structure for each standard unit
        model.standardRates.forEach(stdRate => {
          const unit = stdRate.unit;
          if (!fullTankRatesByUnit[unit]) {
            fullTankRatesByUnit[unit] = { rates: [], metadata: [] };
          }
        });
        
        // Process full tank rates and match to standard units based on meter type
        if (fullTankRates.length > 0) {
          fullTankRates.forEach(rateData => {
            // Handle both object format (with metadata) and number format (for backward compatibility)
            const rate = typeof rateData === 'object' ? rateData.rate : rateData;
            if (rate <= 0) return;
            
            // Determine which standard unit this rate belongs to based on meter name
            let matchedUnit = null;
            if (typeof rateData === 'object' && rateData.meterName) {
              const meterName = rateData.meterName.toUpperCase();
              
              // Check if meter matches any standard unit
              // Priority: KWH first (most specific), then KM, then Hours
              for (const stdRate of model.standardRates) {
                const unitUpper = stdRate.unit.toUpperCase();
                
                // Check KWH first (most specific)
                if (unitUpper === 'L/KWH') {
                  if (meterName.includes('KWH') || meterName.includes('KILOWATT')) {
                    matchedUnit = stdRate.unit;
                    break; // KWH is most specific, so break immediately
                  }
                }
              }
              
              // If KWH not matched, check KM
              if (!matchedUnit) {
                for (const stdRate of model.standardRates) {
                  const unitUpper = stdRate.unit.toUpperCase();
                  if (unitUpper === 'L/KM' || unitUpper === 'L/KMS') {
                    if (meterName.includes('KM') || meterName.includes('KMS') || 
                        meterName.includes('ODO') || meterName.includes('DISTANCE')) {
                      matchedUnit = stdRate.unit;
                      break;
                    }
                  }
                }
              }
              
              // If still not matched, check Hours
              if (!matchedUnit) {
                for (const stdRate of model.standardRates) {
                  const unitUpper = stdRate.unit.toUpperCase();
                  if (unitUpper === 'L/HOURS' || unitUpper === 'LTR/HOURS' || unitUpper === 'L/HRS') {
                    if (meterName.includes('HOUR') || meterName.includes('HRS')) {
                      matchedUnit = stdRate.unit;
                      break;
                    }
                  }
                }
              }
            }
            
            // If no match found, skip this rate to avoid incorrect matching
            // We should only calculate rates for meters that match defined standard units
            if (!matchedUnit) {
              console.warn(`Could not match meter "${rateData.meterName || 'unknown'}" to any standard unit for model ${model.modelName}. Available standard units: ${model.standardRates.map(r => r.unit).join(', ')}`);
              return; // Skip this rate - don't assign to wrong unit
            }
            
            // Add to the appropriate unit group
            if (matchedUnit && fullTankRatesByUnit[matchedUnit]) {
              fullTankRatesByUnit[matchedUnit].rates.push(rate);
              if (typeof rateData === 'object' && rateData.rate !== undefined) {
                fullTankRatesByUnit[matchedUnit].metadata.push(rateData);
              } else {
                fullTankRatesByUnit[matchedUnit].metadata.push(null);
              }
            }
          });
        }

        // Create one row for each standard rate
        model.standardRates.forEach(stdRate => {
          const unit = stdRate.unit;
          const unitUpper = unit.toUpperCase();
          
          // Get IoT rates for this unit (only if available in IoT data)
          const iotRatesLPerUnit = iotRatesByUnit[unit] ? iotRatesByUnit[unit].lPerUnit : [];
          const iotRatesUnitPerL = iotRatesByUnit[unit] ? iotRatesByUnit[unit].unitPerL : [];
          
          // Get Full Tank rates for this unit
          const fullTankRatesLPerUnit = fullTankRatesByUnit[unit] ? fullTankRatesByUnit[unit].rates : [];
          const fullTankRatesUnitPerL = fullTankRatesLPerUnit.map(r => 1 / r);
          const fullTankRatesMetadata = fullTankRatesByUnit[unit] ? fullTankRatesByUnit[unit].metadata : [];

          // Filter out zeros (dropZero = true, always)
          const iotRatesLPerUnitFiltered = iotRatesLPerUnit.filter(r => r > 0);
          const iotRatesUnitPerLFiltered = iotRatesUnitPerL.filter(r => r > 0);
          const fullTankRatesLPerUnitFiltered = fullTankRatesLPerUnit.filter(r => r > 0);
          const fullTankRatesUnitPerLFiltered = fullTankRatesUnitPerL.filter(r => r > 0);

          console.log(`Model ${model.modelName} (${unit}): IoT L/Unit rates: ${iotRatesLPerUnitFiltered.length}, Full Tank L/Unit rates: ${fullTankRatesLPerUnitFiltered.length}`);

          // Calculate HDI for IoT data (minimum 5 rows required, dropZero = true)
          let iotHdiLPerUnit = createEmptyStats(iotRatesLPerUnitFiltered.length);
          let iotHdiUnitPerL = createEmptyStats(iotRatesUnitPerLFiltered.length);
          
          if (iotRatesLPerUnitFiltered.length >= 5) {
            iotHdiLPerUnit = computeStatsHDIAdaptive(iotRatesLPerUnitFiltered, true);
          }
          
          if (iotRatesUnitPerLFiltered.length >= 5) {
            iotHdiUnitPerL = computeStatsHDIAdaptive(iotRatesUnitPerLFiltered, true);
          }

          // Calculate HDI for Full Tank data (minimum 5 rows required, dropZero = true)
          let fullTankHdiLPerUnit = createEmptyStats(fullTankRatesLPerUnitFiltered.length);
          let fullTankHdiUnitPerL = createEmptyStats(fullTankRatesUnitPerLFiltered.length);
          
          if (fullTankRatesLPerUnitFiltered.length >= 5) {
            fullTankHdiLPerUnit = computeStatsHDIAdaptive(fullTankRatesLPerUnitFiltered, true);
          }
          
          if (fullTankRatesUnitPerLFiltered.length >= 5) {
            fullTankHdiUnitPerL = computeStatsHDIAdaptive(fullTankRatesUnitPerLFiltered, true);
          }

          // Extract unit name for display
          const unitName = unit.replace(/^L\//i, '').replace(/^LTR\//i, '');
          const inverseUnit = `${unitName}/L`;

          resultsData.push({
            modelId: model.modelId,
            modelName: model.modelName,
            standardConsumptionRate: stdRate.rate,
            standardConsumptionRateUnit: unit,
            hdiLPerUnit: iotHdiLPerUnit.hdi_average,
            hdiUnitPerL: iotHdiUnitPerL.hdi_average,
            fullTankHdiLPerUnit: fullTankHdiLPerUnit.hdi_average,
            fullTankHdiUnitPerL: fullTankHdiUnitPerL.hdi_average,
            dataPoints: iotRatesLPerUnitFiltered.length,
            fullTankDataPoints: fullTankRatesLPerUnitFiltered.length,
            errors: iotRatesLPerUnitFiltered.length < 5 && unitUpper !== 'L/KWH' ? `Insufficient IoT data points (${iotRatesLPerUnitFiltered.length} < 5)` : (unitUpper === 'L/KWH' ? 'KWH not available in IoT data' : null),
            // Store raw rates for download
            rawRatesLPerUnit: iotRatesLPerUnitFiltered,
            rawRatesUnitPerL: iotRatesUnitPerLFiltered,
            rawFullTankRatesLPerUnit: fullTankRatesLPerUnitFiltered,
            rawFullTankRatesUnitPerL: fullTankRatesUnitPerLFiltered,
            rawFullTankRatesMetadata: fullTankRatesLPerUnitFiltered.map((rate, idx) => {
              // Find the corresponding metadata for this rate
              const originalIdx = fullTankRatesLPerUnit.indexOf(rate);
              return originalIdx >= 0 && originalIdx < fullTankRatesMetadata.length ? fullTankRatesMetadata[originalIdx] : null;
            }),
            // Variability metrics for IoT
            iotStdDev: iotHdiLPerUnit.std_dev,
            iotCvPercent: iotHdiLPerUnit.cv_percent,
            iotHdiLower: iotHdiLPerUnit.hdi_lower,
            iotHdiUpper: iotHdiLPerUnit.hdi_upper,
            iotHdiRange: iotHdiLPerUnit.hdi_range,
            iotCoverageUsed: iotHdiLPerUnit.coverage_used,
            iotOutliersCount: iotHdiLPerUnit.outliers_count,
            iotOutliersPercent: iotHdiLPerUnit.outliers_percent,
            iotMean: iotHdiLPerUnit.mean,
            // Variability metrics for Full Tank
            fullTankStdDev: fullTankHdiLPerUnit.std_dev,
            fullTankCvPercent: fullTankHdiLPerUnit.cv_percent,
            fullTankHdiLower: fullTankHdiLPerUnit.hdi_lower,
            fullTankHdiUpper: fullTankHdiLPerUnit.hdi_upper,
            fullTankHdiRange: fullTankHdiLPerUnit.hdi_range,
            fullTankCoverageUsed: fullTankHdiLPerUnit.coverage_used,
            fullTankOutliersCount: fullTankHdiLPerUnit.outliers_count,
            fullTankOutliersPercent: fullTankHdiLPerUnit.outliers_percent,
            fullTankMean: fullTankHdiLPerUnit.mean,
          });
        });
      }

      setResults(resultsData);
      message.success(`Processed ${resultsData.length} asset models`);
    } catch (error) {
      console.error('Error processing data:', error);
      message.error(`Error processing data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditStandardRate = (model) => {
    setEditingModel(model);
    setEditStandardRate(model.standardConsumptionRate || null);
    setEditUnit(model.standardConsumptionRateUnit || 'KM/L');
    setEditModalVisible(true);
  };

  const saveStandardRate = async () => {
    if (!editingModel || !editStandardRate || !editUnit) {
      message.warning('Please enter standard consumption rate and unit');
      return;
    }

    // TODO: Implement API call to save standard consumption rate
    // For now, update local state
    setAssetModels(prev => prev.map(m => 
      m.modelId === editingModel.modelId
        ? { ...m, standardConsumptionRate: editStandardRate, standardConsumptionRateUnit: editUnit }
        : m
    ));

    setEditModalVisible(false);
    setEditingModel(null);
    message.success('Standard consumption rate saved');
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
      return 'N/A';
    }
    if (typeof value === 'number') {
      return value.toFixed(4);
    }
    return String(value);
  };

  const downloadRawHDIData = (record) => {
    if (!record.rawRatesLPerUnit || record.rawRatesLPerUnit.length === 0) {
      message.warning('No raw data available for this model');
      return;
    }

    const unitName = record.standardConsumptionRateUnit ? 
      record.standardConsumptionRateUnit.replace(/^L\//i, '').replace(/^LTR\//i, '') : '';
    const inverseUnit = `${unitName}/L`;

    // Create CSV with both L/Unit and Unit/L rates
    const csvRows = [
      `Model,${record.modelName}`,
      `Data Source,IoT Data`,
      `Standard Consumption Rate,${record.standardConsumptionRate} ${record.standardConsumptionRateUnit}`,
      `HDI Average (${record.standardConsumptionRateUnit}),${formatValue(record.hdiLPerUnit)}`,
      `HDI Average (${inverseUnit}),${formatValue(record.hdiUnitPerL)}`,
      `Data Points,${record.dataPoints}`,
      '',
      'Index,Consumption Rate (L/Unit),Consumption Rate (Unit/L)',
      ...record.rawRatesLPerUnit.map((rate, index) => {
        const unitPerL = record.rawRatesUnitPerL && record.rawRatesUnitPerL[index] 
          ? formatValue(record.rawRatesUnitPerL[index]) 
          : formatValue(1 / rate);
        return `${index + 1},${formatValue(rate)},${unitPerL}`;
      })
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const safeModelName = String(record.modelName || 'unknown').replace(/[^a-z0-9]/gi, '_').substring(0, 30);
    const timestamp = new Date().getTime();
    const filename = `iot_hdi_raw_data_${safeModelName}_${timestamp}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success(`Downloaded ${record.rawRatesLPerUnit.length} IoT data points`);
  };

  const downloadRawFullTankData = (record) => {
    if (!record.rawFullTankRatesLPerUnit || record.rawFullTankRatesLPerUnit.length === 0) {
      message.warning('No raw full tank data available for this model');
      return;
    }

    const unitName = record.standardConsumptionRateUnit ? 
      record.standardConsumptionRateUnit.replace(/^L\//i, '').replace(/^LTR\//i, '') : '';
    const inverseUnit = `${unitName}/L`;

    // Create CSV with detailed information including voucher numbers
    const csvRows = [
      `Model,${record.modelName}`,
      `Data Source,Full Tank to Full Tank`,
      `Standard Consumption Rate,${record.standardConsumptionRate} ${record.standardConsumptionRateUnit}`,
      `HDI Average (${record.standardConsumptionRateUnit}),${formatValue(record.fullTankHdiLPerUnit)}`,
      `HDI Average (${inverseUnit}),${formatValue(record.fullTankHdiUnitPerL)}`,
      `Data Points,${record.fullTankDataPoints}`,
      '',
    ];

    // Check if we have metadata
    const hasMetadata = record.rawFullTankRatesMetadata && record.rawFullTankRatesMetadata.length > 0;

    if (hasMetadata) {
      // Detailed format with voucher numbers and calculation details
      csvRows.push('Index,From Voucher,To Voucher,From Date,To Date,Consumption Rate (L/Unit),Consumption Rate (Unit/L),Fuel Consumed (L),Run Value,Meter Name');
      
      record.rawFullTankRatesLPerUnit.forEach((rate, index) => {
        const metadata = record.rawFullTankRatesMetadata[index];
        const unitPerL = record.rawFullTankRatesUnitPerL && record.rawFullTankRatesUnitPerL[index] 
          ? formatValue(record.rawFullTankRatesUnitPerL[index]) 
          : formatValue(1 / rate);
        
        if (metadata) {
          csvRows.push(
            `${index + 1},"${metadata.fromVoucher}","${metadata.toVoucher}","${metadata.fromDate}","${metadata.toDate}",${formatValue(rate)},${unitPerL},${formatValue(metadata.fuelConsumed)},${formatValue(metadata.runValue)},"${metadata.meterName}"`
          );
        } else {
          csvRows.push(`${index + 1},N/A,N/A,N/A,N/A,${formatValue(rate)},${unitPerL},N/A,N/A,N/A`);
        }
      });

      // Add detailed fuel entries section
      csvRows.push('');
      csvRows.push('=== Detailed Fuel Entries for Each Calculation ===');
      csvRows.push('');
      
      record.rawFullTankRatesLPerUnit.forEach((rate, index) => {
        const metadata = record.rawFullTankRatesMetadata[index];
        if (metadata && metadata.fuelEntries && metadata.fuelEntries.length > 0) {
          csvRows.push(`Calculation ${index + 1}: From ${metadata.fromVoucher} to ${metadata.toVoucher}`);
          csvRows.push('Voucher Number,Date,Quantity (L),Is Full Tank');
          metadata.fuelEntries.forEach(entry => {
            csvRows.push(`"${entry.voucherNo}","${entry.date}",${formatValue(entry.quantity)},${entry.isFullTank ? 'Yes' : 'No'}`);
          });
          csvRows.push(`Total Fuel Consumed,${formatValue(metadata.fuelConsumed)} L`);
          csvRows.push(`Run Value,${formatValue(metadata.runValue)} ${metadata.meterName}`);
          csvRows.push(`Consumption Rate,${formatValue(rate)} ${record.standardConsumptionRateUnit}`);
          csvRows.push('');
        }
      });
    } else {
      // Simple format without metadata
      csvRows.push('Index,Consumption Rate (L/Unit),Consumption Rate (Unit/L)');
      record.rawFullTankRatesLPerUnit.forEach((rate, index) => {
        const unitPerL = record.rawFullTankRatesUnitPerL && record.rawFullTankRatesUnitPerL[index] 
          ? formatValue(record.rawFullTankRatesUnitPerL[index]) 
          : formatValue(1 / rate);
        csvRows.push(`${index + 1},${formatValue(rate)},${unitPerL}`);
      });
    }

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const safeModelName = String(record.modelName || 'unknown').replace(/[^a-z0-9]/gi, '_').substring(0, 30);
    const timestamp = new Date().getTime();
    const filename = `fulltank_hdi_raw_data_${safeModelName}_${timestamp}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success(`Downloaded ${record.rawFullTankRatesLPerUnit.length} full tank data points`);
  };

  // Function to download table data as CSV
  const downloadTableCSV = (data) => {
    if (!data || data.length === 0) {
      message.warning('No data to download');
      return;
    }

    // Define CSV headers
    const headers = [
      'Asset Model',
      'Asset Type',
      'Asset Sub Type',
      'Unit',
      'Standard Consumption Rate (L/Unit)',
      'Standard Consumption Rate (Unit/L)',
      'Full Tank HDI Average (L/Unit)',
      'Full Tank HDI Average (Unit/L)',
      'Full Tank Data Points',
      'Full Tank CV (%)',
      'Full Tank Std Dev',
      'Full Tank HDI Range',
      'Full Tank HDI Lower',
      'Full Tank HDI Upper',
      'Full Tank Outliers Count',
      'Full Tank Outliers (%)',
      'IoT HDI Average (L/Unit)',
      'IoT HDI Average (Unit/L)',
      'IoT Data Points',
      'IoT CV (%)',
      'IoT Std Dev',
      'IoT HDI Range',
      'IoT HDI Lower',
      'IoT HDI Upper',
      'IoT Outliers Count',
      'IoT Outliers (%)',
      'Full Tank Difference from Standard (L/Unit)',
      'Full Tank Difference from Standard (%)',
      'IoT Difference from Standard (L/Unit)',
      'IoT Difference from Standard (%)',
      'Status'
    ];

    // Convert data to CSV rows
    const csvRows = data.map(record => {
      const unitName = record.standardConsumptionRateUnit ? 
        record.standardConsumptionRateUnit.replace(/^L\//i, '').replace(/^LTR\//i, '') : '';
      const inverseUnit = `${unitName}/L`;
      
      // Standard Rate
      const standardLPerUnit = record.standardConsumptionRate || null;
      const standardUnitPerL = standardLPerUnit > 0 ? (1 / standardLPerUnit) : null;
      
      // Full Tank HDI
      const fullTankLPerUnit = record.fullTankHdiLPerUnit;
      const fullTankUnitPerL = record.fullTankHdiUnitPerL;
      const fullTankDataPoints = record.rawFullTankRatesLPerUnit ? record.rawFullTankRatesLPerUnit.length : 0;
      
      // IoT HDI
      const iotLPerUnit = record.hdiLPerUnit;
      const iotUnitPerL = record.hdiUnitPerL;
      const iotDataPoints = record.rawRatesLPerUnit ? record.rawRatesLPerUnit.length : 0;
      
      // Full Tank Difference
      let fullTankDiffLPerUnit = null;
      let fullTankDiffPercent = null;
      if (standardLPerUnit !== null && !isNaN(standardLPerUnit) && fullTankLPerUnit !== null && !isNaN(fullTankLPerUnit)) {
        fullTankDiffLPerUnit = fullTankLPerUnit - standardLPerUnit;
        fullTankDiffPercent = (fullTankDiffLPerUnit / standardLPerUnit) * 100;
      }
      
      // IoT Difference
      let iotDiffLPerUnit = null;
      let iotDiffPercent = null;
      if (standardLPerUnit !== null && !isNaN(standardLPerUnit) && iotLPerUnit !== null && !isNaN(iotLPerUnit)) {
        iotDiffLPerUnit = iotLPerUnit - standardLPerUnit;
        iotDiffPercent = (iotDiffLPerUnit / standardLPerUnit) * 100;
      }
      
      // Status
      let status = 'N/A';
      if (record.standardConsumptionRate && record.standardConsumptionRateUnit) {
        if (fullTankLPerUnit !== null && !isNaN(fullTankLPerUnit)) {
          status = 'Full Tank Available';
        } else if (iotLPerUnit !== null && !isNaN(iotLPerUnit)) {
          status = 'IoT Available';
        } else {
          status = 'No Data';
        }
      } else {
        status = 'Standard Rate Not Defined';
      }
      
      // Get asset type and sub type from assetModels
      const model = assetModels.find(m => m.modelId === record.modelId);
      const assetType = model?.assetTypeName || 'N/A';
      const assetSubType = model?.assetSubTypeName || 'N/A';
      
      return [
        record.modelName || 'N/A',
        assetType,
        assetSubType,
        record.standardConsumptionRateUnit || 'N/A',
        standardLPerUnit !== null ? formatValue(standardLPerUnit) : 'N/A',
        standardUnitPerL !== null ? formatValue(standardUnitPerL) : 'N/A',
        fullTankLPerUnit !== null && !isNaN(fullTankLPerUnit) ? formatValue(fullTankLPerUnit) : 'N/A',
        fullTankUnitPerL !== null && !isNaN(fullTankUnitPerL) ? formatValue(fullTankUnitPerL) : 'N/A',
        fullTankDataPoints,
        // Full Tank Variability
        record.fullTankCvPercent !== null && !isNaN(record.fullTankCvPercent) ? formatValue(record.fullTankCvPercent) : 'N/A',
        record.fullTankStdDev !== null && !isNaN(record.fullTankStdDev) ? formatValue(record.fullTankStdDev) : 'N/A',
        record.fullTankHdiRange !== null && !isNaN(record.fullTankHdiRange) ? formatValue(record.fullTankHdiRange) : 'N/A',
        record.fullTankHdiLower !== null && !isNaN(record.fullTankHdiLower) ? formatValue(record.fullTankHdiLower) : 'N/A',
        record.fullTankHdiUpper !== null && !isNaN(record.fullTankHdiUpper) ? formatValue(record.fullTankHdiUpper) : 'N/A',
        record.fullTankOutliersCount !== null && !isNaN(record.fullTankOutliersCount) ? record.fullTankOutliersCount : 'N/A',
        record.fullTankOutliersPercent !== null && !isNaN(record.fullTankOutliersPercent) ? formatValue(record.fullTankOutliersPercent) : 'N/A',
        // IoT HDI
        iotLPerUnit !== null && !isNaN(iotLPerUnit) ? formatValue(iotLPerUnit) : 'N/A',
        iotUnitPerL !== null && !isNaN(iotUnitPerL) ? formatValue(iotUnitPerL) : 'N/A',
        iotDataPoints,
        // IoT Variability
        record.iotCvPercent !== null && !isNaN(record.iotCvPercent) ? formatValue(record.iotCvPercent) : 'N/A',
        record.iotStdDev !== null && !isNaN(record.iotStdDev) ? formatValue(record.iotStdDev) : 'N/A',
        record.iotHdiRange !== null && !isNaN(record.iotHdiRange) ? formatValue(record.iotHdiRange) : 'N/A',
        record.iotHdiLower !== null && !isNaN(record.iotHdiLower) ? formatValue(record.iotHdiLower) : 'N/A',
        record.iotHdiUpper !== null && !isNaN(record.iotHdiUpper) ? formatValue(record.iotHdiUpper) : 'N/A',
        record.iotOutliersCount !== null && !isNaN(record.iotOutliersCount) ? record.iotOutliersCount : 'N/A',
        record.iotOutliersPercent !== null && !isNaN(record.iotOutliersPercent) ? formatValue(record.iotOutliersPercent) : 'N/A',
        // Differences
        fullTankDiffLPerUnit !== null ? formatValue(fullTankDiffLPerUnit) : 'N/A',
        fullTankDiffPercent !== null ? `${formatValue(fullTankDiffPercent)}%` : 'N/A',
        iotDiffLPerUnit !== null ? formatValue(iotDiffLPerUnit) : 'N/A',
        iotDiffPercent !== null ? `${formatValue(iotDiffPercent)}%` : 'N/A',
        status
      ];
    });

    // Create CSV content
    const csvContent = [
      headers.map(h => `"${h}"`).join(','),
      ...csvRows.map(row => row.map(cell => {
        const cellValue = cell === null || cell === undefined ? '' : String(cell);
        // Escape quotes and wrap in quotes
        return `"${cellValue.replace(/"/g, '""')}"`;
      }).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const dateRangeStr = dateRange && dateRange.length === 2
      ? `${moment(dateRange[0]).format('YYYY-MM-DD')}_to_${moment(dateRange[1]).format('YYYY-MM-DD')}`
      : moment().format('YYYY-MM-DD');
    
    link.setAttribute('href', url);
    link.setAttribute('download', `HDI_Consumption_Comparison_${dateRangeStr}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    message.success(`Downloaded ${data.length} records as CSV`);
  };

  const columns = [
    {
      title: 'Asset Model',
      dataIndex: 'modelName',
      key: 'modelName',
      width: 200,
      fixed: 'left',
    },
    {
      title: 'Standard Consumption Rate',
      key: 'standardRate',
      width: 280,
      render: (text, record) => {
        if (!record.standardConsumptionRate || !record.standardConsumptionRateUnit) {
          return (
            <span style={{ color: '#999', fontStyle: 'italic' }}>
              Not defined
              {record.standardConsumptionRateUnit
                ? ` (using ${record.standardConsumptionRateUnit.replace(/^L\//i, '')} from meters)`
                : ''}
            </span>
          );
        }
        
        // Calculate inverse unit (Unit/L from L/Unit)
        const lPerUnit = record.standardConsumptionRate;
        const unitPerL = lPerUnit > 0 ? (1 / lPerUnit) : null;
        
        // Extract unit name from L/Unit format (e.g., "L/KM" -> "KM")
        const unitName = record.standardConsumptionRateUnit.replace(/^L\//i, '').replace(/^LTR\//i, '');
        const inverseUnit = `${unitName}/L`;
        
        return (
          <div>
            <div>
              <strong>{formatValue(lPerUnit)} {record.standardConsumptionRateUnit}</strong>
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {unitPerL !== null ? `${formatValue(unitPerL)} ${inverseUnit}` : 'N/A'}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Full Tank HDI Average',
      key: 'fullTankHdiAverage',
      width: 200,
      align: 'right',
      render: (text, record) => {
        const hdiLPerUnit = record.fullTankHdiLPerUnit;
        const hdiUnitPerL = record.fullTankHdiUnitPerL;
        const unitName = record.standardConsumptionRateUnit ? 
          record.standardConsumptionRateUnit.replace(/^L\//i, '').replace(/^LTR\//i, '') : '';
        const inverseUnit = `${unitName}/L`;
        const hasData = record.rawFullTankRatesLPerUnit && record.rawFullTankRatesLPerUnit.length > 0;
        
        return (
          <div
            style={{
              cursor: hasData ? 'pointer' : 'default',
            }}
            onClick={() => {
              if (hasData) {
                downloadRawFullTankData(record);
              }
            }}
            title={hasData ? 'Click to download raw data' : ''}
          >
            <div>
              {hdiLPerUnit !== null && !isNaN(hdiLPerUnit) ? (
                <strong 
                  style={{ 
                    color: '#1890ff',
                    textDecoration: hasData ? 'underline' : 'none'
                  }}
                >
                  {formatValue(hdiLPerUnit)} {record.standardConsumptionRateUnit || ''}
                </strong>
              ) : (
                <span style={{ color: '#999' }}>N/A</span>
              )}
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px' }}>
              {hdiUnitPerL !== null && !isNaN(hdiUnitPerL) ? (
                <span 
                  style={{ 
                    color: '#52c41a',
                    textDecoration: hasData ? 'underline' : 'none'
                  }}
                >
                  {formatValue(hdiUnitPerL)} {inverseUnit}
                </span>
              ) : (
                <span style={{ color: '#999', fontSize: '12px' }}>N/A</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'IoT HDI Average',
      key: 'iotHdiAverage',
      width: 200,
      align: 'right',
      render: (text, record) => {
        const hdiLPerUnit = record.hdiLPerUnit;
        const hdiUnitPerL = record.hdiUnitPerL;
        const unitName = record.standardConsumptionRateUnit ? 
          record.standardConsumptionRateUnit.replace(/^L\//i, '').replace(/^LTR\//i, '') : '';
        const inverseUnit = `${unitName}/L`;
        const hasData = record.rawRatesLPerUnit && record.rawRatesLPerUnit.length > 0;
        
        return (
          <div
            style={{
              cursor: hasData ? 'pointer' : 'default',
            }}
            onClick={() => {
              if (hasData) {
                downloadRawHDIData(record);
              }
            }}
            title={hasData ? 'Click to download raw data' : ''}
          >
            <div>
              {hdiLPerUnit !== null && !isNaN(hdiLPerUnit) ? (
                <strong 
                  style={{ 
                    color: '#1890ff',
                    textDecoration: hasData ? 'underline' : 'none'
                  }}
                >
                  {formatValue(hdiLPerUnit)} {record.standardConsumptionRateUnit || ''}
                </strong>
              ) : (
                <span style={{ color: '#999' }}>N/A</span>
              )}
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px' }}>
              {hdiUnitPerL !== null && !isNaN(hdiUnitPerL) ? (
                <span 
                  style={{ 
                    color: '#52c41a',
                    textDecoration: hasData ? 'underline' : 'none'
                  }}
                >
                  {formatValue(hdiUnitPerL)} {inverseUnit}
                </span>
              ) : (
                <span style={{ color: '#999', fontSize: '12px' }}>N/A</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'IoT Difference from Standard',
      key: 'iotDifference',
      width: 220,
      align: 'right',
      render: (text, record) => {
        const standardRate = record.standardConsumptionRate;
        const hdiLPerUnit = record.hdiLPerUnit;
        const hdiUnitPerL = record.hdiUnitPerL;
        const unitName = record.standardConsumptionRateUnit ? 
          record.standardConsumptionRateUnit.replace(/^L\//i, '').replace(/^LTR\//i, '') : '';
        const inverseUnit = `${unitName}/L`;
        
        // Calculate standard rate in Unit/L format
        const standardUnitPerL = standardRate > 0 ? (1 / standardRate) : null;
        
        // Calculate differences
        // Since L/Unit and Unit/L are inverses, we use L/Unit percentage for both
        // and apply the same color logic (higher L/Unit = worse = red)
        let diffLPerUnit = null;
        let diffUnitPerL = null;
        let diffPercentLPerUnit = null;
        let diffPercentUnitPerL = null;
        
        if (standardRate !== null && !isNaN(standardRate) && hdiLPerUnit !== null && !isNaN(hdiLPerUnit)) {
          diffLPerUnit = hdiLPerUnit - standardRate;
          diffPercentLPerUnit = (diffLPerUnit / standardRate) * 100;
        }
        
        if (standardUnitPerL !== null && !isNaN(standardUnitPerL) && hdiUnitPerL !== null && !isNaN(hdiUnitPerL)) {
          diffUnitPerL = hdiUnitPerL - standardUnitPerL;
          // Calculate percentage based on L/Unit to ensure consistency
          // Unit/L percentage will be approximately the negative of L/Unit percentage
          diffPercentUnitPerL = standardRate > 0 && hdiLPerUnit > 0 
            ? ((1/hdiLPerUnit - 1/standardRate) / (1/standardRate)) * 100
            : (diffUnitPerL / standardUnitPerL) * 100;
        }
        
        // Color logic: Use L/Unit difference to determine color for both
        // Higher L/Unit = worse = Red, Lower L/Unit = better = Green
        // Both formats should have the same color since they represent the same relationship
        const getColor = (diffLPerUnit) => {
          if (diffLPerUnit === null || isNaN(diffLPerUnit)) return '#999';
          return diffLPerUnit > 0 ? '#ff4d4f' : '#52c41a'; // Red if higher (worse), Green if lower (better)
        };
        
        const color = getColor(diffLPerUnit);
        
        return (
          <div>
            <div>
              {diffLPerUnit !== null && !isNaN(diffLPerUnit) ? (
                <span style={{ color: color }}>
                  {diffLPerUnit > 0 ? '+' : ''}{formatValue(diffLPerUnit)} ({diffPercentLPerUnit > 0 ? '+' : ''}{formatValue(diffPercentLPerUnit)}%)
                </span>
              ) : (
                <span style={{ color: '#999' }}>N/A</span>
              )}
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px' }}>
              {diffUnitPerL !== null && !isNaN(diffUnitPerL) ? (
                <span style={{ color: color }}>
                  {diffUnitPerL > 0 ? '+' : ''}{formatValue(diffUnitPerL)} ({diffPercentUnitPerL > 0 ? '+' : ''}{formatValue(diffPercentUnitPerL)}%)
                </span>
              ) : (
                <span style={{ color: '#999', fontSize: '12px' }}>N/A</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Full Tank Difference from Standard',
      key: 'fullTankDifference',
      width: 220,
      align: 'right',
      render: (text, record) => {
        const standardRate = record.standardConsumptionRate;
        const fullTankHdiLPerUnit = record.fullTankHdiLPerUnit;
        const fullTankHdiUnitPerL = record.fullTankHdiUnitPerL;
        const unitName = record.standardConsumptionRateUnit ? 
          record.standardConsumptionRateUnit.replace(/^L\//i, '').replace(/^LTR\//i, '') : '';
        const inverseUnit = `${unitName}/L`;
        
        // Calculate standard rate in Unit/L format
        const standardUnitPerL = standardRate > 0 ? (1 / standardRate) : null;
        
        // Calculate differences for Full Tank
        // Since L/Unit and Unit/L are inverses, we use L/Unit percentage for both
        // and apply the same color logic (higher L/Unit = worse = red)
        let diffLPerUnit = null;
        let diffUnitPerL = null;
        let diffPercentLPerUnit = null;
        let diffPercentUnitPerL = null;
        
        if (standardRate !== null && !isNaN(standardRate) && fullTankHdiLPerUnit !== null && !isNaN(fullTankHdiLPerUnit)) {
          diffLPerUnit = fullTankHdiLPerUnit - standardRate;
          diffPercentLPerUnit = (diffLPerUnit / standardRate) * 100;
        }
        
        if (standardUnitPerL !== null && !isNaN(standardUnitPerL) && fullTankHdiUnitPerL !== null && !isNaN(fullTankHdiUnitPerL)) {
          diffUnitPerL = fullTankHdiUnitPerL - standardUnitPerL;
          // Calculate percentage based on L/Unit to ensure consistency
          // Unit/L percentage will be approximately the negative of L/Unit percentage
          diffPercentUnitPerL = standardRate > 0 && fullTankHdiLPerUnit > 0 
            ? ((1/fullTankHdiLPerUnit - 1/standardRate) / (1/standardRate)) * 100
            : (diffUnitPerL / standardUnitPerL) * 100;
        }
        
        // Color logic: Use L/Unit difference to determine color for both
        // Higher L/Unit = worse = Red, Lower L/Unit = better = Green
        // Both formats should have the same color since they represent the same relationship
        const getColor = (diffLPerUnit) => {
          if (diffLPerUnit === null || isNaN(diffLPerUnit)) return '#999';
          return diffLPerUnit > 0 ? '#ff4d4f' : '#52c41a'; // Red if higher (worse), Green if lower (better)
        };
        
        const color = getColor(diffLPerUnit);
        
        return (
          <div>
            <div>
              {diffLPerUnit !== null && !isNaN(diffLPerUnit) ? (
                <span style={{ color: color }}>
                  {diffLPerUnit > 0 ? '+' : ''}{formatValue(diffLPerUnit)} ({diffPercentLPerUnit > 0 ? '+' : ''}{formatValue(diffPercentLPerUnit)}%)
                </span>
              ) : (
                <span style={{ color: '#999' }}>N/A</span>
              )}
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px' }}>
              {diffUnitPerL !== null && !isNaN(diffUnitPerL) ? (
                <span style={{ color: color }}>
                  {diffUnitPerL > 0 ? '+' : ''}{formatValue(diffUnitPerL)} ({diffPercentUnitPerL > 0 ? '+' : ''}{formatValue(diffPercentUnitPerL)}%)
                </span>
              ) : (
                <span style={{ color: '#999', fontSize: '12px' }}>N/A</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'IoT Data Points',
      dataIndex: 'dataPoints',
      key: 'dataPoints',
      width: 130,
      align: 'right',
    },
    {
      title: 'IoT Data Variability',
      key: 'iotVariability',
      width: 180,
      align: 'left',
      render: (text, record) => {
        const cv = record.iotCvPercent;
        const stdDev = record.iotStdDev;
        const outliers = record.iotOutliersCount;
        const outliersPercent = record.iotOutliersPercent;
        const hdiRange = record.iotHdiRange;
        const hdiLower = record.iotHdiLower;
        const hdiUpper = record.iotHdiUpper;
        const coverage = record.iotCoverageUsed;
        const mean = record.iotMean;
        const hasData = record.dataPoints >= 5 && !isNaN(cv);
        
        if (!hasData) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        
        // Determine CV color: Green if < 20%, Yellow if 20-40%, Red if > 40%
        const getCvColor = (cvVal) => {
          if (cvVal === null || isNaN(cvVal)) return '#999';
          if (cvVal < 20) return '#52c41a';
          if (cvVal < 40) return '#faad14';
          return '#ff4d4f';
        };
        
        const cvColor = getCvColor(cv);
        
        return (
          <div style={{ fontSize: '12px' }}>
            <div title={`Coefficient of Variation: SD/Mean × 100%. Lower = more consistent data.`}>
              <strong>CV:</strong> <span style={{ color: cvColor }}>{formatValue(cv)}%</span>
            </div>
            <div title={`Standard Deviation of raw data`}>
              <strong>SD:</strong> {formatValue(stdDev)}
            </div>
            <div title={`HDI Range: ${formatValue(hdiLower)} - ${formatValue(hdiUpper)}`}>
              <strong>HDI Range:</strong> {formatValue(hdiRange)}
            </div>
            <div title={`Values outside HDI bounds (Coverage: ${formatValue(coverage)}%)`}>
              <strong>Outliers:</strong> {outliers} ({formatValue(outliersPercent)}%)
            </div>
          </div>
        );
      },
    },
    {
      title: 'Full Tank Data Points',
      dataIndex: 'fullTankDataPoints',
      key: 'fullTankDataPoints',
      width: 150,
      align: 'right',
    },
    {
      title: 'Full Tank Data Variability',
      key: 'fullTankVariability',
      width: 180,
      align: 'left',
      render: (text, record) => {
        const cv = record.fullTankCvPercent;
        const stdDev = record.fullTankStdDev;
        const outliers = record.fullTankOutliersCount;
        const outliersPercent = record.fullTankOutliersPercent;
        const hdiRange = record.fullTankHdiRange;
        const hdiLower = record.fullTankHdiLower;
        const hdiUpper = record.fullTankHdiUpper;
        const coverage = record.fullTankCoverageUsed;
        const mean = record.fullTankMean;
        const hasData = record.fullTankDataPoints >= 5 && !isNaN(cv);
        
        if (!hasData) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        
        // Determine CV color: Green if < 20%, Yellow if 20-40%, Red if > 40%
        const getCvColor = (cvVal) => {
          if (cvVal === null || isNaN(cvVal)) return '#999';
          if (cvVal < 20) return '#52c41a';
          if (cvVal < 40) return '#faad14';
          return '#ff4d4f';
        };
        
        const cvColor = getCvColor(cv);
        
        return (
          <div style={{ fontSize: '12px' }}>
            <div title={`Coefficient of Variation: SD/Mean × 100%. Lower = more consistent data.`}>
              <strong>CV:</strong> <span style={{ color: cvColor }}>{formatValue(cv)}%</span>
            </div>
            <div title={`Standard Deviation of raw data`}>
              <strong>SD:</strong> {formatValue(stdDev)}
            </div>
            <div title={`HDI Range: ${formatValue(hdiLower)} - ${formatValue(hdiUpper)}`}>
              <strong>HDI Range:</strong> {formatValue(hdiRange)}
            </div>
            <div title={`Values outside HDI bounds (Coverage: ${formatValue(coverage)}%)`}>
              <strong>Outliers:</strong> {outliers} ({formatValue(outliersPercent)}%)
            </div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: (text, record) => {
        if (record.errors) {
          return <Tag color="red">Insufficient Data</Tag>;
        }
        if (record.hdiLPerUnit === null && record.hdiUnitPerL === null) {
          return <Tag color="orange">No Data</Tag>;
        }
        return <Tag color="green">Complete</Tag>;
      },
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <Card
        title={
          <Title level={3} style={{ margin: 0 }}>
            HDI Consumption Rate Comparison
          </Title>
        }
      >
        <Alert
          message="HDI Consumption Rate Analysis"
          description="This tool compares HDI (Highest Density Interval) averages calculated from Full Tank to Full Tank data and IoT data for each asset model. Standard consumption rates must be defined for each model to calculate HDI values."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card size="small" title="Date Range">
              <RangePicker
                style={{ width: '100%' }}
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
                allowClear
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" title="Filters">
              <div style={{ marginBottom: 8 }}>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>Asset Type:</Text>
                <Select
                  style={{ width: '100%' }}
                  placeholder="All Asset Types"
                  value={selectedAssetType}
                  onChange={(value) => {
                    setSelectedAssetType(value);
                    // Reset sub type and model when type changes
                    setSelectedAssetSubType(null);
                    setSelectedAssetModel(null);
                  }}
                  allowClear
                >
                  {assetTypes.map(type => (
                    <Option key={type.id} value={type.id}>{type.name}</Option>
                  ))}
                </Select>
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>Asset Sub Type:</Text>
                <Select
                  style={{ width: '100%' }}
                  placeholder="All Asset Sub Types"
                  value={selectedAssetSubType}
                  onChange={(value) => {
                    setSelectedAssetSubType(value);
                    // Reset model when sub type changes
                    setSelectedAssetModel(null);
                  }}
                  allowClear
                  disabled={!selectedAssetType}
                >
                  {assetSubTypes
                    .filter(st => !selectedAssetType || st.assetTypeId === selectedAssetType)
                    .map(subType => (
                      <Option key={subType.id} value={subType.id}>{subType.name}</Option>
                    ))}
                </Select>
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>Asset Model:</Text>
                <Select
                  style={{ width: '100%' }}
                  placeholder="All Asset Models"
                  value={selectedAssetModel}
                  onChange={setSelectedAssetModel}
                  allowClear
                  disabled={!selectedAssetSubType}
                  showSearch
                  filterOption={(input, option) => {
                    const children = option.props.children;
                    if (typeof children === 'string') {
                      return children.toLowerCase().includes(input.toLowerCase());
                    }
                    return false;
                  }}
                >
                  {assetModels
                    .filter(model => {
                      if (selectedAssetType && model.assetTypeId !== selectedAssetType) return false;
                      if (selectedAssetSubType && model.assetSubTypeId !== selectedAssetSubType) return false;
                      return true;
                    })
                    .map(model => (
                      <Option key={model.modelId} value={model.modelId}>{model.modelName}</Option>
                    ))}
                </Select>
              </div>
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" title="Actions">
              <Button
                type="primary"
                icon="reload"
                onClick={processData}
                loading={loading}
                disabled={!dateRange || dateRange.length !== 2}
                style={{ width: '100%', marginBottom: 8 }}
              >
                Calculate HDI
              </Button>
              <Button
                style={{ width: '100%', marginBottom: 8 }}
                icon="reload"
                onClick={loadAssetModels}
                loading={loadingModels}
              >
                Reload Models
              </Button>
            </Card>
          </Col>
        </Row>

        {loadingModels ? (
          <Spin tip="Loading asset models...">
            <div style={{ height: 200 }} />
          </Spin>
        ) : (
          <>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Text strong>Total Asset Models: </Text>
              <Text>{assetModels.length}</Text>
              {results.length > 0 && (
                <span style={{ marginLeft: 16 }}>
                  <Text strong>Results: </Text>
                  <Text>{results.length}</Text>
                </span>
              )}
              {(selectedAssetType || selectedAssetSubType || selectedAssetModel) && (
                <span style={{ marginLeft: 16 }}>
                  <Text strong>Filtered Results: </Text>
                  <Text>{results.filter(r => {
                    const model = assetModels.find(m => m.modelId === r.modelId);
                    if (!model) return false;
                    if (selectedAssetType && model.assetTypeId !== selectedAssetType) return false;
                    if (selectedAssetSubType && model.assetSubTypeId !== selectedAssetSubType) return false;
                    if (selectedAssetModel && model.modelId !== selectedAssetModel) return false;
                    return true;
                  }).length}</Text>
                </span>
              )}
            </Card>

            {results.length > 0 && (
              <Card 
                title="HDI Analysis Results"
                extra={
                  <Button
                    type="primary"
                    icon="download"
                    onClick={() => {
                      const filteredResults = results.filter(r => {
                        const model = assetModels.find(m => m.modelId === r.modelId);
                        if (!model) return false;
                        if (selectedAssetType && model.assetTypeId !== selectedAssetType) return false;
                        if (selectedAssetSubType && model.assetSubTypeId !== selectedAssetSubType) return false;
                        if (selectedAssetModel && model.modelId !== selectedAssetModel) return false;
                        return true;
                      });
                      downloadTableCSV(filteredResults);
                    }}
                  >
                    Download CSV
                  </Button>
                }
              >
                <Table
                  columns={columns}
                  dataSource={results
                    .filter(r => {
                      const model = assetModels.find(m => m.modelId === r.modelId);
                      if (!model) return false;
                      if (selectedAssetType && model.assetTypeId !== selectedAssetType) return false;
                      if (selectedAssetSubType && model.assetSubTypeId !== selectedAssetSubType) return false;
                      if (selectedAssetModel && model.modelId !== selectedAssetModel) return false;
                      return true;
                    })
                    .map((r, idx) => ({ ...r, key: idx }))}
                  scroll={{ x: 1400, y: 600 }}
                  pagination={{
                    pageSize: 50,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} models`
                  }}
                />
              </Card>
            )}
          </>
        )}

        <Modal
          title="Define Standard Consumption Rate"
          visible={editModalVisible}
          onOk={saveStandardRate}
          onCancel={() => {
            setEditModalVisible(false);
            setEditingModel(null);
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <Text strong>Asset Model: </Text>
            <Text>{editingModel?.modelName}</Text>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text strong>Standard Consumption Rate:</Text>
            <InputNumber
              style={{ width: '100%', marginTop: 8 }}
              value={editStandardRate}
              onChange={setEditStandardRate}
              min={0}
              step={0.1}
              placeholder="Enter standard consumption rate"
            />
          </div>
          <div>
            <Text strong>Unit:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={editUnit}
              onChange={setEditUnit}
            >
              <Option value="KM/L">KM/L</Option>
              <Option value="L/HOURS">L/HOURS</Option>
            </Select>
          </div>
        </Modal>
      </Card>
    </div>
  );
}

export default HDIConsumptionComparison;






